import { ZONES } from "../data/city";
import {
  DURATION,
  RESOURCE_LIMIT,
  STEP,
  SYSTEMS,
  type Allocation,
  type Decision,
  type Incident,
  type Parameters,
  type Run,
  type Snapshot,
  type System,
  type ZoneId,
} from "./types";
export const clamp = (n: number, min = 0, max = 100) =>
  Math.min(max, Math.max(min, n));
export const round = (n: number) => Math.round(n * 10) / 10;
export function emptyAllocation(): Allocation {
  return Object.fromEntries(
    ZONES.map((z) => [z.id, { water: 0, transport: 0, power: 0 }]),
  ) as Allocation;
}
export function allocationsAt(
  decisions: Decision[],
  minute: number,
): Allocation {
  const allocation = emptyAllocation();
  for (const d of decisions)
    if (d.minute <= minute) allocation[d.zone][d.system] += d.delta;
  return allocation;
}
export const allocatedTotal = (a: Allocation) =>
  Object.values(a).reduce((sum, v) => sum + v.water + v.transport + v.power, 0);
export function canDecide(
  decisions: Decision[],
  minute: number,
  zone: ZoneId,
  system: System,
  delta: 1 | -1,
): boolean {
  const next = [
    ...decisions,
    { id: "candidate", minute, zone, system, delta },
  ].sort((a, b) => a.minute - b.minute);
  const a = emptyAllocation();
  for (const d of next) {
    a[d.zone][d.system] += d.delta;
    if (
      a[d.zone][d.system] < 0 ||
      a[d.zone][d.system] > 3 ||
      allocatedTotal(a) > RESOURCE_LIMIT
    )
      return false;
  }
  return true;
}
function activeSupport(
  decisions: Decision[],
  minute: number,
  zone: ZoneId,
  system: System,
): number {
  const starts: number[] = [];
  for (const d of decisions)
    if (d.zone === zone && d.system === system && d.minute <= minute) {
      if (d.delta === 1) starts.push(d.minute);
      else starts.shift();
    }
  return starts.reduce((s, t) => s + clamp((minute - t - 5) / 20, 0, 1), 0);
}
export function sanitiseParameters(p: Parameters): Parameters {
  return {
    rain: clamp(p.rain, 0, 60),
    wind: clamp(p.wind, 0, 100),
    disruption: clamp(p.disruption),
    demand: clamp(p.demand, 80, 180),
  };
}
/** A toy stress model, not a hydraulic, transport or power-flow solver. All coefficients are synthetic. */
export function simulate(
  parameters: Parameters,
  decisions: Decision[] = [],
  until = DURATION,
): Snapshot[] {
  const p = sanitiseParameters(parameters);
  const ordered = [...decisions].sort((a, b) => a.minute - b.minute);
  let previous = ZONES.map((z) => ({
    id: z.id,
    water: 0,
    transport: 0,
    power: 0,
    affected: 0,
    continuity: 100,
  }));
  let hours = 0;
  const history: Snapshot[] = [];
  for (let minute = 0; minute <= clamp(until, 0, DURATION); minute += STEP) {
    const envelope =
      minute <= 45
        ? 0.35 + (0.65 * minute) / 45
        : minute <= 75
          ? 1
          : Math.max(0.12, 1 - (minute - 75) / 65);
    const allocation = allocationsAt(ordered, minute);
    const zones = ZONES.map((z, i) => {
      const support = (s: System) => activeSupport(ordered, minute, z.id, s);
      const waterTarget = clamp(
        ((p.rain / 60) * 74 * z.floodExposure +
          Math.max(0, p.wind - 40) * 0.11) *
          envelope -
          support("water") * 23,
      );
      const water = round(previous[i].water * 0.48 + waterTarget * 0.52);
      const powerTarget = clamp(
        (Math.max(0, p.demand - 105) * 0.86 * z.powerExposure +
          Math.max(0, p.wind - 35) * 0.33) *
          envelope +
          water * 0.29 -
          support("power") * 24,
      );
      const power = round(previous[i].power * 0.48 + powerTarget * 0.52);
      const transportTarget = clamp(
        p.disruption * 0.59 * z.transportExposure * envelope +
          water * 0.46 +
          power * 0.3 +
          Math.max(0, p.wind - 55) * 0.1 * envelope -
          support("transport") * 25,
      );
      const transport = round(
        previous[i].transport * 0.48 + transportTarget * 0.52,
      );
      const combined = clamp(water * 0.33 + power * 0.4 + transport * 0.27);
      return {
        id: z.id,
        water,
        power,
        transport,
        affected: Math.round(((z.households * combined) / 100) * 0.68),
        continuity: round(100 - combined),
      };
    });
    const weight = ZONES.reduce((s, z) => s + z.households, 0);
    const mean = (key: "continuity" | "power" | "transport") =>
      round(
        zones.reduce((s, z, i) => s + z[key] * ZONES[i].households, 0) / weight,
      );
    const affected = zones.reduce((s, z) => s + z.affected, 0);
    if (minute > 0)
      hours +=
        (((affected + history[history.length - 1].affected) / 2) * STEP) / 60;
    const titles: Record<System, string> = {
      water: "Surface water pressure",
      transport: "Connections under strain",
      power: "Local supply pressure",
    };
    const incidents: Incident[] = zones
      .flatMap((z) =>
        SYSTEMS.filter((s) => z[s] >= 18).map((s) => ({
          id: `${z.id}:${s}`,
          zone: z.id,
          system: s,
          severity: z[s],
          title: titles[s],
          crews: allocation[z.id][s],
          activeCrews: round(activeSupport(ordered, minute, z.id, s)),
        })),
      )
      .sort((a, b) => b.severity - a.severity || a.id.localeCompare(b.id));
    history.push({
      minute,
      zones,
      incidents,
      continuity: mean("continuity"),
      mobility: round(100 - mean("transport")),
      energy: round(100 - mean("power")),
      affected,
      disruptionHours: Math.round(hours),
      allocations: allocation,
    });
    previous = zones;
  }
  return history;
}
export function evaluateRun(run: Run) {
  const history = simulate(run.parameters, run.decisions);
  return {
    history,
    current: history[Math.round(clamp(run.minute, 0, DURATION) / STEP)],
    baseline: simulate(run.parameters),
  };
}
export function validateRun(value: unknown): Run | null {
  if (!value || typeof value !== "object") return null;
  const r = value as Run;
  if (
    !["calm", "storm", "rail", "energy"].includes(r.scenario) ||
    !r.parameters ||
    !Array.isArray(r.decisions) ||
    r.decisions.length > 500 ||
    !Number.isFinite(r.minute) ||
    r.minute < 0 ||
    r.minute > DURATION ||
    r.minute % STEP
  )
    return null;
  if (
    !["rain", "wind", "disruption", "demand"].every((k) =>
      Number.isFinite(r.parameters[k as keyof Parameters]),
    )
  )
    return null;
  const p = sanitiseParameters(r.parameters);
  if (JSON.stringify(p) !== JSON.stringify(r.parameters)) {
    if (
      Object.keys(p).some(
        (k) => p[k as keyof Parameters] !== r.parameters[k as keyof Parameters],
      )
    )
      return null;
  }
  let ds: Decision[] = [];
  if (
    r.decisions.some(
      (d) => !d || typeof d !== "object" || !Number.isFinite(d.minute),
    )
  )
    return null;
  const ordered = [...r.decisions].sort((a, b) => a.minute - b.minute);
  for (const d of ordered) {
    if (
      !d ||
      typeof d.id !== "string" ||
      !ZONES.some((z) => z.id === d.zone) ||
      !SYSTEMS.includes(d.system) ||
      ![1, -1].includes(d.delta) ||
      !Number.isFinite(d.minute) ||
      d.minute < 0 ||
      d.minute > DURATION ||
      d.minute % STEP ||
      !canDecide(ds, d.minute, d.zone, d.system, d.delta)
    )
      return null;
    ds.push({
      id: d.id,
      minute: d.minute,
      zone: d.zone,
      system: d.system,
      delta: d.delta,
    });
  }
  return {
    parameters: p,
    decisions: ds,
    scenario: r.scenario,
    minute: Math.round(clamp(r.minute, 0, DURATION) / STEP) * STEP,
  };
}
